import { Fragment } from 'react';

export interface MarkMatchesProps {
  /** The text content to search within */
  text: string;
  /** The search term(s) to highlight - supports space-separated terms when highlightAllTerms=true */
  search: string;
  /** CSS class to apply to highlighted matches (applied to <mark> elements) */
  matchClassName?: string;
  /** Enable partial matching - "Skibb" matches "Skibby" (default: true) */
  partialMatch?: boolean;
  /** Split search by spaces and highlight each term separately (default: true) */
  highlightAllTerms?: boolean;
  /** Minimum character length for a term to be highlighted - filters out short terms (default: 3) */
  minMatchLength?: number;
}

function MarkMatches({
  text,
  search,
  matchClassName,
  partialMatch = true,
  highlightAllTerms = true,
  minMatchLength = 3,
}: MarkMatchesProps) {
  if (!search) return text;

  try {
    const searchTerms = highlightAllTerms
      ? search.trim().split(/\s+/).filter(Boolean)
      : [search];

    const validTerms = searchTerms.filter(
      (term) => term.length >= minMatchLength,
    );

    if (validTerms.length === 0) return text;

    const escapedTerms = validTerms.map((term) =>
      term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    );

    const patterns = escapedTerms.map((term) =>
      partialMatch ? term : `^${term}$`,
    );

    const combinedPattern = patterns.join('|');
    const regex = new RegExp(`(${combinedPattern})`, 'gi');
    const split = text.split(regex);

    // Create a non-global regex for testing to avoid stateful lastIndex issues
    const testRegex = new RegExp(`^(${combinedPattern})$`, 'i');

    return (
      <>
        {split.filter(String).map((part, index) => {
          const isMatch = testRegex.test(part);
          // Reset lastIndex to prevent stateful behavior
          testRegex.lastIndex = 0;
          return isMatch ? (
            <mark key={part + index} className={matchClassName}>
              {part}
            </mark>
          ) : (
            <Fragment key={part + index}>{part}</Fragment>
          );
        })}
      </>
    );
  } catch {
    // If search is not a valid regex, return original text
    return text;
  }
}

export default MarkMatches;
